import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building, Phone, MapPin, Globe, Calendar, Loader2, Save, X } from "lucide-react";
import { formatDateLong } from "@/lib/utils";
import { formatPhoneNumber } from "@/lib/phone-utils";
import type { Company } from "@/api/generated/client/models/Company";
import type { UpdateCompanyForm } from "@/schemas/company";

interface CompanyInformationCardProps {
  company: Company | undefined;
  isEditing: boolean;
  formData: UpdateCompanyForm;
  isPending: boolean;
  isAdmin: boolean;
  onFormDataChange: (data: UpdateCompanyForm) => void;
  onEdit: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
}

export function CompanyInformationCard({
  company,
  isEditing,
  formData,
  isPending,
  isAdmin,
  onFormDataChange,
  onEdit,
  onSubmit,
  onCancel,
}: CompanyInformationCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Company Information
            </CardTitle>
            <CardDescription>
              Manage your company details and contact information
            </CardDescription>
          </div>
          {!isEditing && isAdmin && (
            <Button onClick={onEdit}>
              Edit Account
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Company Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              Company Name
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name || ""}
              onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
              disabled={!isEditing}
              placeholder="Enter company name"
              required
            />
          </div>

         
          {/* Company Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Company Phone
            </Label>
            <Input
              id="phone"
              type="tel"
              value={isEditing ? (formData.phone || "") : formatPhoneNumber(company?.phone)}
              onChange={(e) => onFormDataChange({ ...formData, phone: e.target.value })}
              disabled={!isEditing}
              placeholder="+1 (555) 000-0000"
            />
          </div>

          {/* Company Address */}
          <div className="space-y-2">
            <Label htmlFor="address" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Company Address
            </Label>
            <Textarea
              id="address"
              value={formData.address || ""}
              onChange={(e) => onFormDataChange({ ...formData, address: e.target.value })}
              disabled={!isEditing}
              placeholder="Enter company address"
              rows={3}
            />
          </div>

          {/* Company Domain */}
          <div className="space-y-2">
            <Label htmlFor="domain" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Company Domain
            </Label>
            <Input
              id="domain"
              type="text"
              value={formData.domain || ""}
              onChange={(e) => onFormDataChange({ ...formData, domain: e.target.value })}
              disabled={!isEditing}
              placeholder="example.com"
            />
            <p className="text-xs text-muted-foreground">
              Your company's website domain
            </p>
          </div>

          {/* Account Created (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="createdAt" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Account Created
            </Label>
            <Input
              id="createdAt"
              type="text"
              value={company?.createdAt ? formatDateLong(company.createdAt) : ""}
              disabled
              className="bg-muted"
            />
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={isPending}
                className="flex-1"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
